import { supabase, getProdSupabaseClient } from './client';
import { translateSystemError, isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';

interface DealerPreferenceValue {
  is_included: boolean;
  notes?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
  created_by?: string | null;
  modified_by?: string | null;
}

function dealerPreferenceIsExcluded(pref: boolean | DealerPreferenceValue | undefined): boolean {
  if (pref === undefined) return false;
  if (pref === false) return true;
  return typeof pref === 'object' && 'is_included' in pref && !pref['is_included'];
}

function dealerPreferenceFromUnknownObject(v: Record<string, unknown>): DealerPreferenceValue {
  return {
    is_included: v['is_included'] !== false,
    notes: (typeof v['notes'] === 'string' ? v['notes'] : null) || null,
    created_at: (typeof v['created_at'] === 'string' ? v['created_at'] : null) || null,
    modified_at: (typeof v['modified_at'] === 'string' ? v['modified_at'] : null) || null,
    created_by: (typeof v['created_by'] === 'string' ? v['created_by'] : null) || null,
    modified_by: (typeof v['modified_by'] === 'string' ? v['modified_by'] : null) || null
  };
}

/** Merge one user_preferences row into tenant-wide dealer map; excluded wins over included. */
function applyTenantDealerPreferenceRow(
  preferences: Record<string, boolean | DealerPreferenceValue>,
  item: { entity_id: string; preference_value: unknown }
): void {
  const normalizedEntityId = item.entity_id?.trim() || item.entity_id;
  if (dealerPreferenceIsExcluded(preferences[normalizedEntityId])) return;

  const value = item.preference_value;
  if (typeof value === 'boolean') {
    preferences[normalizedEntityId] = value;
    return;
  }
  if (typeof value !== 'object' || value === null) return;
  preferences[normalizedEntityId] = dealerPreferenceFromUnknownObject(
    value as Record<string, unknown>
  );
}

export const preferencesAPI = {
  // Dealer Preferences
  async getDealerPreferences(userId: string) {
    if (!userId) {
      console.warn('No userId provided to getDealerPreferences');
      return {};
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn('No tenant_id available for getting dealer preferences');
        return {};
      }

      // Use prod schema for user_preferences
      // Dealer exclusions are tenant-wide: fetch ALL users' dealer preferences for this tenant
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('user_preferences')
        .select('entity_id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer');

      if (error) {
        logSystemError(error, 'fetching dealer preferences');
        throw translateSystemError(error, 'fetching dealer preferences');
      }

      // Convert to expected format (tenant-wide: excluded wins when merging rows)
      const preferences: Record<string, boolean | DealerPreferenceValue> = {};
      data?.forEach(item => applyTenantDealerPreferenceRow(preferences, item));

      return preferences;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting dealer preferences');
        throw translateSystemError(error, 'getting dealer preferences');
      }
      throw error;
    }
  },

  async updateDealerPreference(userId: string, dealerName: string, isIncluded: boolean) {
    if (!userId || !dealerName) {
      console.warn('Missing userId or dealerName in updateDealerPreference');
      return;
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('No tenant_id available for updating dealer preference');
      }

      // Normalize dealer name (trim whitespace) for consistent storage
      const normalizedDealerName = dealerName.trim();

      // Use prod schema client
      const prodClient = getProdSupabaseClient();

      // First check if the record exists to preserve existing metadata
      // Try exact match first, then case-insensitive match
      const { data: exactMatch } = await prodClient
        .from('user_preferences')
        .select('preference_value, entity_id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer')
        .eq('entity_id', normalizedDealerName)
        .maybeSingle();

      // If no exact match, try to find case-insensitive match
      let existingData = exactMatch;
      if (!existingData) {
        const { data: allPreferences } = await prodClient
          .from('user_preferences')
          .select('preference_value, entity_id')
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
          .eq('preference_type', 'dealer');

        const matchingPreference = allPreferences?.find(
          p => p.entity_id?.trim().toLowerCase() === normalizedDealerName.toLowerCase()
        );

        if (matchingPreference) {
          existingData = matchingPreference;
        }
      }

      const currentValue = existingData?.preference_value || {};
      const now = new Date().toISOString();

      // Build the new preference value, preserving existing fields
      const newValue = {
        ...currentValue,
        is_included: isIncluded,
        modified_at: now,
        modified_by: userId,
        // Preserve created_at and created_by if they exist
        created_at: currentValue.created_at || now,
        created_by: currentValue.created_by || userId
      };

      // Use upsert to handle both insert and update cases
      // The unique constraint is (tenant_id, user_id, preference_type, entity_id)
      const { error: upsertError } = await prodClient.from('user_preferences').upsert(
        {
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'dealer',
          entity_id: normalizedDealerName,
          preference_value: newValue,
          updated_at: now
        },
        {
          onConflict: 'tenant_id,user_id,preference_type,entity_id'
        }
      );

      // If we found a case-insensitive match with different casing, delete the old one
      if (existingData && existingData.entity_id !== normalizedDealerName) {
        await prodClient
          .from('user_preferences')
          .delete()
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
          .eq('preference_type', 'dealer')
          .eq('entity_id', existingData.entity_id);
      }

      if (upsertError) {
        logSystemError(upsertError, 'upserting dealer preference');
        throw translateSystemError(upsertError, 'upserting dealer preference');
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating dealer preference');
        throw translateSystemError(error, 'updating dealer preference');
      }
      throw error;
    }
  },

  // Update dealer notes - matches original dashboard functionality
  async updateDealerNotes(userId: string, dealerName: string, notesData: any) {
    if (!userId || !dealerName) {
      console.warn('Missing userId or dealerName in updateDealerNotes');
      return;
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('No tenant_id available for updating dealer notes');
      }

      // Use prod schema client
      const prodClient = getProdSupabaseClient();

      // First check if the record exists
      const { data: existingData, error: selectError } = await prodClient
        .from('user_preferences')
        .select('id, preference_value')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer')
        .eq('entity_id', dealerName)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing dealer preference');
        throw translateSystemError(selectError, 'checking existing dealer preference');
      }

      const currentValue = existingData?.preference_value || {};
      const newValue = {
        ...currentValue,
        notes: notesData.notes,
        modified_at: notesData.modified_at,
        modified_by: notesData.modified_by
      };

      if (existingData) {
        // Record exists, update it
        const { error: updateError } = await prodClient
          .from('user_preferences')
          .update({
            preference_value: newValue,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
          .eq('preference_type', 'dealer')
          .eq('entity_id', dealerName);

        if (updateError) {
          console.error('Error updating dealer notes:', updateError);
          throw updateError;
        }
      } else {
        // Record doesn't exist, insert it
        const { error: insertError } = await prodClient.from('user_preferences').insert({
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'dealer',
          entity_id: dealerName,
          preference_value: {
            ...newValue,
            is_included: true, // Default to included
            created_at: notesData.created_at,
            created_by: notesData.created_by
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        if (insertError) {
          // Check if it's a permission error and provide helpful context
          if (insertError.code === '42501' || insertError.code === '403') {
            console.error(
              'Permission denied inserting dealer preference. This may be due to RLS policies. Migration may be needed.'
            );
            console.error('Tenant ID:', tenantId);
            console.error('User ID:', userId);
          }
          console.error('Error inserting dealer notes:', insertError);
          throw insertError;
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating dealer notes');
        throw translateSystemError(error, 'updating dealer notes');
      }
      throw error;
    }
  },

  // Bulk reset all dealer preferences to included
  async resetAllDealerPreferences(userId: string) {
    if (!userId) {
      console.warn('No userId provided to resetAllDealerPreferences');
      return;
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('No tenant_id available for resetting dealer preferences');
      }

      // Use prod schema client
      const prodClient = getProdSupabaseClient();

      // Delete all dealer preferences for this user and tenant
      const { error: deleteError } = await prodClient
        .from('user_preferences')
        .delete()
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer');

      if (deleteError) {
        logSystemError(deleteError, 'resetting dealer preferences');
        throw translateSystemError(deleteError, 'resetting dealer preferences');
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'resetting dealer preferences');
        throw translateSystemError(error, 'resetting dealer preferences');
      }
      throw error;
    }
  },

  // Base Location Preferences
  async getBaseLocation(userId: string) {
    if (!userId) {
      console.warn('No userId provided to getBaseLocation');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preference_value')
        .eq('user_id', userId)
        .eq('preference_type', 'base_location')
        .eq('entity_id', 'user_base')
        .maybeSingle();

      if (error) {
        logSystemError(error, 'fetching base location');
        throw translateSystemError(error, 'fetching base location');
      }

      return data?.preference_value || null;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting base location');
        throw translateSystemError(error, 'getting base location');
      }
      throw error;
    }
  },

  async updateBaseLocation(
    userId: string,
    baseLocation: {
      dealerName: string;
      address: string;
      city: string;
      state: string;
      zipCode: string;
      coordinates?: { lat: number; lng: number };
    }
  ) {
    if (!userId || !baseLocation) {
      console.warn('Missing userId or baseLocation in updateBaseLocation');
      return;
    }

    try {
      const preferenceValue = {
        dealerName: baseLocation.dealerName,
        address: baseLocation.address,
        city: baseLocation.city,
        state: baseLocation.state,
        zipCode: baseLocation.zipCode,
        coordinates: baseLocation.coordinates,
        updated_at: new Date().toISOString()
      };

      // First, try to update existing record
      const { data: updateData, error: updateError } = await supabase
        .from('user_preferences')
        .update({
          preference_value: preferenceValue
        })
        .eq('user_id', userId)
        .eq('preference_type', 'base_location')
        .eq('entity_id', 'user_base')
        .select();

      let error;

      // If update returns no rows (empty array), record doesn't exist - try insert
      if (updateError || !updateData || updateData.length === 0) {
        // If update fails or updates nothing, try to insert (record doesn't exist)
        const insertResult = await supabase
          .from('user_preferences')
          .insert({
            user_id: userId,
            preference_type: 'base_location',
            entity_id: 'user_base',
            preference_value: preferenceValue
          })
          .select();

        error = insertResult.error;
      } else {
        error = updateError;
      }

      if (error) {
        logSystemError(error, 'updating base location');
        throw translateSystemError(error, 'updating base location');
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating base location');
        throw translateSystemError(error, 'updating base location');
      }
      throw error;
    }
  },

  // Campaign Preferences
  async getCampaignPreferences(userId: string) {
    if (!userId) {
      console.warn('No userId provided to getCampaignPreferences');
      return {
        skillLevels: {},
        mobileEligibility: {},
        campaignStatus: {},
        campaignStatusReasons: {}
      };
    }

    try {
      // Get tenant ID for tenant scoping
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn('No tenant ID available for getCampaignPreferences');
        return {
          skillLevels: {},
          mobileEligibility: {},
          campaignStatus: {},
          campaignStatusReasons: {}
        };
      }

      // Use prod schema for user_preferences with tenant_id
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('user_preferences')
        .select('entity_id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('preference_type', 'campaign');

      if (error) {
        logSystemError(error, 'fetching campaign preferences');
        throw translateSystemError(error, 'fetching campaign preferences');
      }

      const skillLevels: Record<string, number | null> = {};
      const mobileEligibility: Record<string, boolean> = {};
      const campaignStatus: Record<string, 'Available' | 'Advance Notice' | 'Paused' | null> = {};
      const campaignStatusReasons: Record<string, string | null> = {};

      data?.forEach(item => {
        const value = item.preference_value;
        if (value.skill_level !== undefined) {
          skillLevels[item.entity_id] = value.skill_level;
        }
        if (value.mobile_servicing_eligible !== undefined) {
          mobileEligibility[item.entity_id] = value.mobile_servicing_eligible;
        }
        if (value.status !== undefined) {
          campaignStatus[item.entity_id] = value.status;
        }
        if (value.status_reason !== undefined) {
          campaignStatusReasons[item.entity_id] = value.status_reason;
        }
      });

      return { skillLevels, mobileEligibility, campaignStatus, campaignStatusReasons };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting campaign preferences');
        throw translateSystemError(error, 'getting campaign preferences');
      }
      throw error;
    }
  },

  async updateCampaignSkillLevel(
    userId: string,
    campaignNumber: string,
    skillLevel: number | null
  ) {
    if (!userId || !campaignNumber) {
      console.warn('Missing userId or campaignNumber in updateCampaignSkillLevel');
      return;
    }

    try {
      // Get tenant ID for tenant scoping
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      // Use prod schema for user_preferences with tenant_id
      const prodClient = getProdSupabaseClient();

      // First check if the record exists
      const { data: existingData, error: selectError } = await prodClient
        .from('user_preferences')
        .select('id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('preference_type', 'campaign')
        .eq('entity_id', campaignNumber)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing campaign preference');
        throw translateSystemError(selectError, 'checking existing campaign preference');
      }

      const currentValue = existingData?.preference_value || {};
      const newValue = {
        ...currentValue,
        skill_level: skillLevel,
        selected: currentValue.selected !== undefined ? currentValue.selected : false // Required by check constraint
      };

      if (existingData) {
        // Record exists, update it
        const { error: updateError } = await prodClient
          .from('user_preferences')
          .update({
            preference_value: newValue
          })
          .eq('tenant_id', tenantId)
          .eq('user_id', userId)
          .eq('preference_type', 'campaign')
          .eq('entity_id', campaignNumber);

        if (updateError) {
          logSystemError(updateError, 'updating campaign skill level');
          throw translateSystemError(updateError, 'updating campaign skill level');
        }
      } else {
        // Record doesn't exist, insert it with tenant_id
        const { error: insertError } = await prodClient.from('user_preferences').insert({
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'campaign',
          entity_id: campaignNumber,
          preference_value: newValue,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        if (insertError) {
          // Check if it's a permission error and provide helpful context
          if (insertError.code === '42501') {
            console.error(
              'Permission denied inserting campaign preference. This may be due to RLS policies. Migration may be needed.'
            );
            console.error('Tenant ID:', tenantId);
            console.error('User ID:', userId);
            console.error('Campaign Number:', campaignNumber);
            console.error('Error details:', insertError);

            // Try to get JWT info for debugging
            try {
              const {
                data: { session }
              } = await supabase.auth.getSession();
              if (session?.access_token) {
                // Decode JWT payload (base64 decode the middle part)
                const parts = session.access_token.split('.');
                if (parts.length === 3) {
                  const payload = JSON.parse(atob(parts[1]));
                  console.error('JWT tenant_id:', payload.tenant_id);
                  console.error('JWT sub (user_id):', payload.sub);
                  console.error('JWT role:', payload.role);
                }
              }
            } catch (e) {
              console.error('Could not decode JWT for debugging');
            }
          }
          logSystemError(insertError, 'inserting campaign skill level');
          throw translateSystemError(insertError, 'inserting campaign skill level');
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating campaign skill level');
        throw translateSystemError(error, 'updating campaign skill level');
      }
      throw error;
    }
  },

  async updateCampaignMobileServicingEligibility(
    userId: string,
    campaignNumber: string,
    mobileEligibility: boolean
  ) {
    if (!userId || !campaignNumber) {
      console.warn('Missing userId or campaignNumber in updateCampaignMobileServicingEligibility');
      return;
    }

    try {
      // Get tenant ID for tenant scoping
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      // Use prod schema for user_preferences with tenant_id
      const prodClient = getProdSupabaseClient();

      // First check if the record exists
      const { data: existingData, error: selectError } = await prodClient
        .from('user_preferences')
        .select('id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('preference_type', 'campaign')
        .eq('entity_id', campaignNumber)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing campaign preference');
        throw translateSystemError(selectError, 'checking existing campaign preference');
      }

      const currentValue = existingData?.preference_value || {};
      const newValue = {
        ...currentValue,
        mobile_servicing_eligible: mobileEligibility,
        selected: currentValue.selected !== undefined ? currentValue.selected : false // Required by check constraint
      };

      if (existingData) {
        // Record exists, update it
        const { error: updateError } = await prodClient
          .from('user_preferences')
          .update({
            preference_value: newValue
          })
          .eq('tenant_id', tenantId)
          .eq('user_id', userId)
          .eq('preference_type', 'campaign')
          .eq('entity_id', campaignNumber);

        if (updateError) {
          logSystemError(updateError, 'updating campaign mobile eligibility');
          throw translateSystemError(updateError, 'updating campaign mobile eligibility');
        }
      } else {
        // Record doesn't exist, insert it with tenant_id
        const { error: insertError } = await prodClient.from('user_preferences').insert({
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'campaign',
          entity_id: campaignNumber,
          preference_value: newValue,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        if (insertError) {
          // Check if it's a permission error and provide helpful context
          if (insertError.code === '42501') {
            console.error(
              'Permission denied inserting campaign preference. This may be due to RLS policies. Migration may be needed.'
            );
            console.error('Tenant ID:', tenantId);
            console.error('User ID:', userId);
          }
          logSystemError(insertError, 'inserting campaign mobile eligibility');
          throw translateSystemError(insertError, 'inserting campaign mobile eligibility');
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating campaign mobile servicing eligibility');
        throw translateSystemError(error, 'updating campaign mobile servicing eligibility');
      }
      throw error;
    }
  },

  async updateCampaignStatus(
    userId: string,
    campaignNumber: string,
    status: 'Available' | 'Advance Notice' | 'Paused',
    statusReason?: string | null
  ) {
    if (!userId || !campaignNumber) {
      console.warn('Missing userId or campaignNumber in updateCampaignStatus');
      return;
    }

    try {
      // Get tenant ID for tenant scoping
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('Tenant ID not available');
      }

      // Use prod schema for user_preferences with tenant_id
      const prodClient = getProdSupabaseClient();

      // First check if the record exists
      const { data: existingData, error: selectError } = await prodClient
        .from('user_preferences')
        .select('id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('preference_type', 'campaign')
        .eq('entity_id', campaignNumber)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing campaign preference');
        throw translateSystemError(selectError, 'checking existing campaign preference');
      }

      const currentValue = existingData?.preference_value || {};
      const newValue = {
        ...currentValue,
        status: status,
        status_reason:
          statusReason !== undefined
            ? statusReason
            : status === 'Paused'
              ? currentValue.status_reason
              : null,
        status_updated_at: new Date().toISOString(),
        selected: currentValue.selected !== undefined ? currentValue.selected : false // Required by check constraint
      };

      if (existingData) {
        // Record exists, update it
        const { error: updateError } = await prodClient
          .from('user_preferences')
          .update({
            preference_value: newValue,
            updated_at: new Date().toISOString()
          })
          .eq('tenant_id', tenantId)
          .eq('user_id', userId)
          .eq('preference_type', 'campaign')
          .eq('entity_id', campaignNumber);

        if (updateError) {
          logSystemError(updateError, 'updating campaign status');
          throw translateSystemError(updateError, 'updating campaign status');
        }
      } else {
        // Record doesn't exist, insert it with tenant_id
        const { error: insertError } = await prodClient.from('user_preferences').insert({
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'campaign',
          entity_id: campaignNumber,
          preference_value: newValue,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        if (insertError) {
          // Check if it's a permission error and provide helpful context
          if (insertError.code === '42501') {
            console.error(
              'Permission denied inserting campaign preference. This may be due to RLS policies. Migration may be needed.'
            );
            console.error('Tenant ID:', tenantId);
            console.error('User ID:', userId);
          }
          logSystemError(insertError, 'inserting campaign status');
          throw translateSystemError(insertError, 'inserting campaign status');
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating campaign status');
        throw translateSystemError(error, 'updating campaign status');
      }
      throw error;
    }
  },

  // Recall Notes Management
  async getRecallNotes(userId: string, recallIds: Array<{ vin: string; campaign: string }>) {
    if (!userId || !recallIds.length) {
      console.warn('No userId or recallIds provided to getRecallNotes');
      return {};
    }

    try {
      const entityIds = recallIds.map(r => `${r.vin}_${r.campaign}`);

      const { data, error } = await supabase
        .from('user_preferences')
        .select('entity_id, preference_value')
        .eq('user_id', userId)
        .eq('preference_type', 'recall_notes')
        .in('entity_id', entityIds);

      if (error) {
        logSystemError(error, 'fetching recall notes');
        throw translateSystemError(error, 'fetching recall notes');
      }

      const notes: Record<string, any> = {};
      data?.forEach(item => {
        notes[item.entity_id] = item.preference_value;
      });

      return notes;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting recall notes');
        throw translateSystemError(error, 'getting recall notes');
      }
      throw error;
    }
  },

  async updateRecallNotes(
    userId: string,
    vin: string,
    campaign: string,
    notesData: {
      advisor_notes?: string;
      technician_notes?: string;
    }
  ) {
    if (!userId || !vin || !campaign) {
      console.warn('Missing required parameters in updateRecallNotes');
      return;
    }

    try {
      const entityId = `${vin}_${campaign}`;

      // First check if the record exists
      const { data: existingData, error: selectError } = await supabase
        .from('user_preferences')
        .select('id, preference_value')
        .eq('user_id', userId)
        .eq('preference_type', 'recall_notes')
        .eq('entity_id', entityId)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing recall notes');
        throw translateSystemError(selectError, 'checking existing recall notes');
      }

      const currentValue = existingData?.preference_value || {};
      const newValue = {
        ...currentValue,
        ...notesData,
        updated_at: new Date().toISOString(),
        updated_by: userId
      };

      // Add creation metadata if this is a new record
      if (!existingData) {
        newValue.created_at = new Date().toISOString();
        newValue.created_by = userId;
      }

      if (existingData) {
        // Record exists, update it
        const { error: updateError } = await supabase
          .from('user_preferences')
          .update({
            preference_value: newValue,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('preference_type', 'recall_notes')
          .eq('entity_id', entityId);

        if (updateError) {
          logSystemError(updateError, 'updating recall notes');
          throw translateSystemError(updateError, 'updating recall notes');
        }
      } else {
        // Record doesn't exist, insert it
        const { error: insertError } = await supabase.from('user_preferences').insert({
          user_id: userId,
          preference_type: 'recall_notes',
          entity_id: entityId,
          preference_value: newValue
        });

        if (insertError) {
          logSystemError(insertError, 'inserting recall notes');
          throw translateSystemError(insertError, 'inserting recall notes');
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating recall notes');
        throw translateSystemError(error, 'updating recall notes');
      }
      throw error;
    }
  },

  async deleteRecallNotes(userId: string, vin: string, campaign: string) {
    if (!userId || !vin || !campaign) {
      console.warn('Missing required parameters in deleteRecallNotes');
      return;
    }

    try {
      const entityId = `${vin}_${campaign}`;

      const { error } = await supabase
        .from('user_preferences')
        .delete()
        .eq('user_id', userId)
        .eq('preference_type', 'recall_notes')
        .eq('entity_id', entityId);

      if (error) {
        logSystemError(error, 'deleting recall notes');
        throw translateSystemError(error, 'deleting recall notes');
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'deleting recall notes');
        throw translateSystemError(error, 'deleting recall notes');
      }
      throw error;
    }
  },

  // Enhanced dealer notes to include technician notes sync
  async getDealerTechnicianNotes(userId: string) {
    if (!userId) {
      console.warn('No userId provided to getDealerTechnicianNotes');
      return {};
    }

    try {
      // Get dealer-level notes from the existing dealer preferences
      const { data, error } = await supabase
        .from('user_preferences')
        .select('entity_id, preference_value')
        .eq('user_id', userId)
        .eq('preference_type', 'dealer');

      if (error) {
        logSystemError(error, 'fetching dealer technician notes');
        throw translateSystemError(error, 'fetching dealer technician notes');
      }

      const notes: Record<string, string> = {};
      data?.forEach(item => {
        // Extract notes from dealer preferences (same as dealer config screen)
        const value = item.preference_value;
        if (typeof value === 'object' && value?.notes) {
          notes[item.entity_id] = value.notes;
        }
      });

      return notes;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting dealer technician notes');
        throw translateSystemError(error, 'getting dealer technician notes');
      }
      throw error;
    }
  },

  async updateDealerTechnicianNotes(userId: string, dealerName: string, notes: string) {
    if (!userId || !dealerName) {
      console.warn('Missing userId or dealerName in updateDealerTechnicianNotes');
      return;
    }

    try {
      // Use the same updateDealerNotes method to ensure sync with dealer config
      const notesData = {
        notes,
        modified_at: new Date().toISOString(),
        modified_by: userId
      };

      await this.updateDealerNotes(userId, dealerName, notesData);
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating dealer technician notes');
        throw translateSystemError(error, 'updating dealer technician notes');
      }
      throw error;
    }
  },

  // Batch get dealer notes for multiple dealers (tenant-shared)
  async getDealerNotesBatch(userId: string, dealerNames: string[]) {
    if (!userId || !dealerNames.length) {
      return {};
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn('[getDealerNotesBatch] No tenant_id available');
        return {};
      }

      // Use prod schema for user_preferences
      // Query by tenant_id to get ALL notes in this tenant (shared across users)
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('user_preferences')
        .select('entity_id, preference_value, updated_at')
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer')
        .in('entity_id', dealerNames)
        .order('updated_at', { ascending: false });

      if (error) {
        logSystemError(error, 'fetching dealer notes batch');
        // Return empty object instead of throwing to avoid breaking UI
        return {};
      }

      const notes: Record<
        string,
        {
          advisor_notes: string;
          technician_notes: string;
          created_at?: string;
          updated_at?: string;
          created_by?: string;
          updated_by?: string;
        }
      > = {};

      // Process results - since we ordered by updated_at DESC, first match per dealer is most recent
      dealerNames.forEach(dealerName => {
        // Find the most recent note for this dealer (first match due to ordering)
        const dealerData = data?.find(d => d.entity_id === dealerName);
        if (dealerData) {
          const value = dealerData.preference_value;
          if (typeof value === 'object' && value !== null) {
            notes[dealerName] = {
              advisor_notes: value.advisor_notes || '',
              technician_notes: value.technician_notes || value.notes || '',
              created_at: value.created_at,
              updated_at: value.modified_at || value.updated_at,
              created_by: value.created_by,
              updated_by: value.modified_by || value.updated_by
            };
          } else {
            notes[dealerName] = { advisor_notes: '', technician_notes: '' };
          }
        } else {
          notes[dealerName] = { advisor_notes: '', technician_notes: '' };
        }
      });

      return notes;
    } catch (error) {
      console.error('Error in getDealerNotesBatch:', error);
      return {};
    }
  },

  // Get dealer-level notes (both advisor and technician notes) - tenant-shared
  async getDealerNotes(userId: string, dealerName: string) {
    if (!userId || !dealerName) {
      console.warn('Missing userId or dealerName in getDealerNotes');
      return { advisor_notes: '', technician_notes: '' };
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn('[getDealerNotes] No tenant_id available');
        return { advisor_notes: '', technician_notes: '' };
      }

      // Use prod schema for user_preferences
      // Query by tenant_id to get notes shared across all users in the tenant
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('user_preferences')
        .select('preference_value')
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer')
        .eq('entity_id', dealerName)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logSystemError(error, 'fetching dealer notes');
        // Return empty notes instead of throwing to avoid breaking the UI
        return { advisor_notes: '', technician_notes: '' };
      }

      if (!data) {
        return { advisor_notes: '', technician_notes: '' };
      }

      const value = data.preference_value;
      if (typeof value === 'object' && value !== null) {
        return {
          advisor_notes: value.advisor_notes || '',
          technician_notes: value.technician_notes || value.notes || '', // Support legacy format
          created_at: value.created_at,
          updated_at: value.modified_at || value.updated_at,
          created_by: value.created_by,
          updated_by: value.modified_by || value.updated_by
        };
      }

      return { advisor_notes: '', technician_notes: '' };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting dealer notes');
        throw translateSystemError(error, 'getting dealer notes');
      }
      throw error;
    }
  },

  // Update dealer-level notes (both advisor and technician notes) - tenant-shared
  async updateDealerLevelNotes(
    userId: string,
    dealerName: string,
    notesData: {
      advisor_notes?: string;
      technician_notes?: string;
    }
  ) {
    if (!userId || !dealerName) {
      console.warn('Missing userId or dealerName in updateDealerLevelNotes');
      return;
    }

    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        throw new Error('No tenant_id available for updating dealer notes');
      }

      // Use prod schema for user_preferences - MUST match getDealerNotesBatch/getDealerNotes
      const prodClient = getProdSupabaseClient();

      // Check if ANY record exists for this tenant+dealer (shared across all users)
      const { data: existingData, error: selectError } = await prodClient
        .from('user_preferences')
        .select('id, user_id, preference_value')
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'dealer')
        .eq('entity_id', dealerName)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (selectError) {
        logSystemError(selectError, 'checking existing dealer preference');
        throw translateSystemError(selectError, 'checking existing dealer preference');
      }

      const currentValue = existingData?.preference_value || {};
      const now = new Date().toISOString();
      const newValue = {
        ...currentValue,
        advisor_notes:
          notesData.advisor_notes !== undefined
            ? notesData.advisor_notes
            : currentValue.advisor_notes || '',
        technician_notes:
          notesData.technician_notes !== undefined
            ? notesData.technician_notes
            : currentValue.technician_notes || currentValue.notes || '',
        // Maintain legacy 'notes' field for backward compatibility (maps to technician_notes)
        notes:
          notesData.technician_notes !== undefined
            ? notesData.technician_notes
            : currentValue.notes || currentValue.technician_notes || '',
        modified_at: now,
        modified_by: userId,
        updated_at: now,
        updated_by: userId,
        // Preserve is_included if it exists
        is_included: currentValue.is_included !== false
      };

      // Add creation metadata if this is a new record
      if (!existingData) {
        newValue.created_at = now;
        newValue.created_by = userId;
      }

      if (existingData) {
        // Record exists for this tenant+dealer, update it by ID
        const { error: updateError } = await prodClient
          .from('user_preferences')
          .update({
            preference_value: newValue,
            updated_at: now
          })
          .eq('id', existingData.id);

        if (updateError) {
          console.error('Error updating dealer notes:', updateError);
          throw updateError;
        }
      } else {
        // No record exists for this tenant+dealer, create one
        const { error: insertError } = await prodClient.from('user_preferences').insert({
          tenant_id: tenantId,
          user_id: userId,
          preference_type: 'dealer',
          entity_id: dealerName,
          preference_value: newValue
        });

        if (insertError) {
          console.error('Error inserting dealer notes:', insertError);
          throw insertError;
        }
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating dealer level notes');
        throw translateSystemError(error, 'updating dealer level notes');
      }
      throw error;
    }
  },

  // What's New Preferences
  async getWhatsNewPreferences(userId: string) {
    if (!userId) {
      console.warn('No userId provided to getWhatsNewPreferences');
      return { lastSeenReleaseId: null };
    }

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('preference_value')
        .eq('user_id', userId)
        .eq('preference_type', 'whats_new')
        .eq('entity_id', 'last_seen_release')
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // No preference set yet (not a system error, expected case)
          return { lastSeenReleaseId: null };
        }
        logSystemError(error, "fetching what's new preferences");
        throw translateSystemError(error, "fetching what's new preferences");
      }

      const value = data?.preference_value;
      if (value && typeof value === 'object' && 'lastSeenReleaseId' in value) {
        return { lastSeenReleaseId: value.lastSeenReleaseId || null };
      }

      // Handle legacy format if it exists
      if (typeof value === 'string') {
        return { lastSeenReleaseId: value };
      }

      return { lastSeenReleaseId: null };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, "getting what's new preferences");
        throw translateSystemError(error, "getting what's new preferences");
      }
      throw error;
    }
  },

  async updateWhatsNewPreferences(userId: string, releaseId: string) {
    if (!userId || !releaseId) {
      console.warn('Missing userId or releaseId in updateWhatsNewPreferences');
      return;
    }

    try {
      const preferenceValue = {
        lastSeenReleaseId: releaseId,
        updated_at: new Date().toISOString()
      };

      // First, try to update existing record
      const { data: updateData, error: updateError } = await supabase
        .from('user_preferences')
        .update({
          preference_value: preferenceValue,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('preference_type', 'whats_new')
        .eq('entity_id', 'last_seen_release')
        .select();

      // If update returns no rows, record doesn't exist - try insert
      if (updateError || !updateData || updateData.length === 0) {
        const { error: insertError } = await supabase.from('user_preferences').insert({
          user_id: userId,
          preference_type: 'whats_new',
          entity_id: 'last_seen_release',
          preference_value: preferenceValue
        });

        if (insertError) {
          logSystemError(insertError, "inserting what's new preferences");
          throw translateSystemError(insertError, "inserting what's new preferences");
        }
      } else if (updateError) {
        logSystemError(updateError, "updating what's new preferences");
        throw translateSystemError(updateError, "updating what's new preferences");
      }
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, "updating what's new preferences");
        throw translateSystemError(error, "updating what's new preferences");
      }
      throw error;
    }
  },

  // Track technician management screen updates
  async updateTechnicianManagementTimestamp(userId: string) {
    if (!userId) {
      console.warn('No userId provided to updateTechnicianManagementTimestamp');
      return;
    }

    try {
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn('No tenant_id available for updating technician management timestamp');
        return;
      }

      const prodClient = getProdSupabaseClient();
      // Note: getProdSupabaseClient() already has db: { schema: 'prod' } configured
      // so we don't need to call .schema('prod') again

      // Check if record exists
      const { data: existingData } = await prodClient
        .from('user_preferences')
        .select('id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .eq('preference_type', 'technician_management')
        .eq('entity_id', 'last_update')
        .single();

      if (existingData) {
        // Update existing record
        const { error } = await prodClient
          .from('user_preferences')
          .update({
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
          .eq('preference_type', 'technician_management')
          .eq('entity_id', 'last_update');

        if (error) {
          logSystemError(error, 'updating technician management timestamp');
          // Don't throw - this is non-critical
          console.warn('Failed to update technician management timestamp:', error);
        }
      } else {
        // Insert new record
        const { error } = await prodClient.from('user_preferences').insert({
          user_id: userId,
          tenant_id: tenantId,
          preference_type: 'technician_management',
          entity_id: 'last_update',
          preference_value: { timestamp: new Date().toISOString() }
        });

        if (error) {
          logSystemError(error, 'inserting technician management timestamp');
          // Don't throw - this is non-critical
          console.warn('Failed to insert technician management timestamp:', error);
        }
      }
    } catch (error) {
      // Don't throw - this is non-critical tracking
      console.warn('Error updating technician management timestamp:', error);
    }
  }
};
